import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Calendar, 
  Tag, Image, Video, FileText, Percent, DollarSign,
  Save, X, AlertCircle, CheckCircle,
  CheckSquare,
  Square,
  Zap,
  Loader2
} from 'lucide-react';
import { promotionsAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const PromotionsManagement = () => {
  const { notify } = useNotificationHook();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'banner',
    content: '',
    image_url: '',
    video_url: '',
    html_content: '',
    discount_percentage: 0,
    discount_amount: 0,
    coupon_code: '',
    valid_from: '',
    valid_until: '',
    show_on_pages: ['product-detail'],
    is_active: true
  });

  // Bulk actions state
  const [selectedPromotions, setSelectedPromotions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch promotions
  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const response = await promotionsAPI.getAll({ admin: true });
      if (response?.data?.success) {
        setPromotions(response.data.data.promotions);
      }
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setError('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPromotion) {
        await promotionsAPI.update(editingPromotion.id, formData);
      } else {
        await promotionsAPI.create(formData);
      }
      
      setShowForm(false);
      setEditingPromotion(null);
      resetForm();
      fetchPromotions();
      notify.success('Promotion saved successfully!');
    } catch (err) {
      console.error('Error saving promotion:', err);
      setError('Failed to save promotion');
      notify.error('Failed to save promotion.');
    }
  };

  // Handle edit
  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      title: promotion.title,
      description: promotion.description,
      type: promotion.type,
      content: promotion.content || '',
      image_url: promotion.image_url || '',
      video_url: promotion.video_url || '',
      html_content: promotion.html_content || '',
      discount_percentage: promotion.discount_percentage || 0,
      discount_amount: promotion.discount_amount || 0,
      coupon_code: promotion.coupon_code || '',
      valid_from: promotion.valid_from ? promotion.valid_from.split('T')[0] : '',
      valid_until: promotion.valid_until ? promotion.valid_until.split('T')[0] : '',
      show_on_pages: promotion.show_on_pages || ['product-detail'],
      is_active: promotion.is_active
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      await promotionsAPI.delete(id);
      fetchPromotions();
      notify.success('Promotion deleted successfully!');
    } catch (err) {
      console.error('Error deleting promotion:', err);
      setError('Failed to delete promotion');
      notify.error('Failed to delete promotion.');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'banner',
      content: '',
      image_url: '',
      video_url: '',
      html_content: '',
      discount_percentage: 0,
      discount_amount: 0,
      coupon_code: '',
      valid_from: '',
      valid_until: '',
      show_on_pages: ['product-detail'],
      is_active: true
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle page selection
  const handlePageToggle = (page) => {
    setFormData(prev => ({
      ...prev,
      show_on_pages: prev.show_on_pages.includes(page)
        ? prev.show_on_pages.filter(p => p !== page)
        : [...prev.show_on_pages, page]
    }));
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'banner': return <Image className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'html': return <FileText className="w-4 h-4" />;
      case 'coupon': return <Percent className="w-4 h-4" />;
      default: return <Tag className="w-4 h-4" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Bulk action functions
  const handleSelectPromotion = (promotionId) => {
    setSelectedPromotions(prev => 
      prev.includes(promotionId) 
        ? prev.filter(id => id !== promotionId)
        : [...prev, promotionId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPromotions([]);
      setSelectAll(false);
    } else {
      setSelectedPromotions(promotions.map(promotion => promotion.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (is_active) => {
    if (selectedPromotions.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await promotionsAPI.bulkUpdateStatus(selectedPromotions, is_active);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedPromotions.length} promotions to ${is_active ? 'active' : 'inactive'} status`);
        setSelectedPromotions([]);
        setSelectAll(false);
        await fetchPromotions();
      } else {
        notify.error('Failed to update promotions: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating promotions:', error);
      notify.error('Error updating promotions: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPromotions.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedPromotions.length} promotions? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await promotionsAPI.bulkDelete(selectedPromotions);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedPromotions.length} promotions`);
        setSelectedPromotions([]);
        setSelectAll(false);
        await fetchPromotions();
      } else {
        notify.error('Failed to delete promotions: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting promotions:', error);
      notify.error('Error deleting promotions: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedPromotions.length > 0);
  }, [selectedPromotions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Promotions Management
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Manage promotional content, coupons, and advertisements
            </p>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingPromotion(null);
              resetForm();
            }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Promotion
          </button>
        </div>

        {/* Bulk Actions */}
        {showBulkActions && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {selectedPromotions.length} promotion{selectedPromotions.length !== 1 ? 's' : ''} selected
                </span>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleBulkStatusUpdate(true)}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
                    Activate
                  </button>
                  
                  <button
                    onClick={() => handleBulkStatusUpdate(false)}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <EyeOff className="h-3 w-3 mr-1" />}
                    Deactivate
                  </button>
                  
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700 dark:text-red-400">{error}</span>
          </div>
        )}

        {/* Promotions Grid */}
        <div className="space-y-4">
          {/* Select All */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {selectAll ? (
                <CheckSquare className="h-4 w-4 text-blue-600" />
              ) : (
                <Square className="h-4 w-4 text-gray-400" />
              )}
              Select All ({promotions.length})
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:p-6">
            {promotions.map((promotion) => (
              <div key={promotion.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => handleSelectPromotion(promotion.id)}
                      className="flex items-center"
                    >
                      {selectedPromotions.includes(promotion.id) ? (
                        <CheckSquare className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Square className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(promotion.type)}
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 capitalize">
                          {promotion.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          promotion.is_active 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {promotion.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {promotion.is_active ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {promotion.title}
                  </h3>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {promotion.description}
                  </p>

                  {/* Type-specific content */}
                  {promotion.type === 'banner' && promotion.image_url && (
                    <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                      <img 
                        src={promotion.image_url} 
                        alt={promotion.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {promotion.type === 'coupon' && promotion.coupon_code && (
                    <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded p-3">
                      <div className="text-center">
                        <span className="text-sm text-primary-600 dark:text-primary-400">Coupon Code</span>
                        <div className="text-lg font-mono font-bold text-primary-700 dark:text-primary-300">
                          {promotion.coupon_code}
                        </div>
                        {promotion.discount_percentage > 0 && (
                          <span className="text-sm text-primary-600 dark:text-primary-400">
                            {promotion.discount_percentage}% OFF
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Validity */}
                  {(promotion.valid_from || promotion.valid_until) && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {promotion.valid_from && formatDate(promotion.valid_from)}
                        {promotion.valid_from && promotion.valid_until && ' - '}
                        {promotion.valid_until && formatDate(promotion.valid_until)}
                      </span>
                    </div>
                  )}

                  {/* Pages */}
                  {promotion.show_on_pages && promotion.show_on_pages.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {promotion.show_on_pages.map((page) => (
                        <span 
                          key={page}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded"
                        >
                          {page}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                  <button
                    onClick={() => handleEdit(promotion)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(promotion.id)}
                    className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Empty State */}
        {promotions.length === 0 && !loading && (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No promotions yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first promotion to start advertising your products
            </p>
            <button
              onClick={() => {
                setShowForm(true);
                setEditingPromotion(null);
                resetForm();
              }}
              className="btn-primary"
            >
              Create Promotion
            </button>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {editingPromotion ? 'Edit Promotion' : 'Add Promotion'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingPromotion(null);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="banner">Banner</option>
                      <option value="video">Video</option>
                      <option value="html">HTML</option>
                      <option value="coupon">Coupon</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Type-specific fields */}
                {formData.type === 'banner' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Image URL
                    </label>
                    <input
                      type="url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                )}

                {formData.type === 'video' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Video URL
                    </label>
                    <input
                      type="url"
                      name="video_url"
                      value={formData.video_url}
                      onChange={handleInputChange}
                      placeholder="https://www.youtube.com/watch?v=VIDEO_ID or direct video file URL"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Supports: YouTube URLs, Vimeo URLs, or direct video file URLs (.mp4, .webm, etc.)
                    </p>
                  </div>
                )}

                {formData.type === 'html' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      HTML Content
                    </label>
                    <textarea
                      name="html_content"
                      value={formData.html_content}
                      onChange={handleInputChange}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                    />
                  </div>
                )}

                {formData.type === 'coupon' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Coupon Code
                      </label>
                      <input
                        type="text"
                        name="coupon_code"
                        value={formData.coupon_code}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Discount %
                      </label>
                      <input
                        type="number"
                        name="discount_percentage"
                        value={formData.discount_percentage}
                        onChange={handleInputChange}
                        min="0"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Discount Amount
                      </label>
                      <input
                        type="number"
                        name="discount_amount"
                        value={formData.discount_amount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                )}

                {/* Validity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valid From
                    </label>
                    <input
                      type="date"
                      name="valid_from"
                      value={formData.valid_from}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valid Until
                    </label>
                    <input
                      type="date"
                      name="valid_until"
                      value={formData.valid_until}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Show on Pages */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Show on Pages
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {['product-detail', 'homepage', 'products', 'services', 'portfolio', 'about', 'contact'].map((page) => (
                      <label key={page} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.show_on_pages.includes(page)}
                          onChange={() => handlePageToggle(page)}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                          {page.replace('-', ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                  />
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="submit"
                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {editingPromotion ? 'Update' : 'Create'} Promotion
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPromotion(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionsManagement;
